// Advanced submission options (DESIGN_SPEC §11.2): the rare SubmitSpec fields the viewer
// almost never changes, tucked behind an "Advanced options…" button on the run sheet to
// keep the default submit path uncluttered.
//
//   command              launch override — defaults to double-click behavior
//   labels               free-form caller tags
//
// allow_input and auto_analyze_children are now surfaced directly in the RunSheet, not
// here — they're common enough that burying them was a UX smell.
//
//   <AdvancedDialog open values onChange onClose />
import { useEffect, useRef } from "react";
import { buttonClass } from "@/components/ui";

export interface AdvancedValues {
  command: string;
  labels: string;
  allow_input: boolean;
  auto_analyze_children: boolean;
}

export const ADVANCED_DEFAULTS: AdvancedValues = {
  command: "",
  labels: "",
  allow_input: false,
  auto_analyze_children: true,
};

export function AdvancedDialog({
  open,
  values,
  onChange,
  onClose,
}: {
  open: boolean;
  values: AdvancedValues;
  onChange: (next: AdvancedValues) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  const patch = <K extends keyof AdvancedValues>(k: K, v: AdvancedValues[K]) => onChange({ ...values, [k]: v });

  return (
    <dialog
      ref={ref}
      aria-labelledby="advanced-title"
      onClose={onClose}
      onKeyDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="m-auto w-[min(540px,calc(100vw-32px))] border border-rule-strong bg-raised p-0 text-ink shadow-pop backdrop:bg-paper/70 open:transition-[opacity,translate] open:duration-160 open:ease-out starting:open:translate-y-1 starting:open:opacity-0"
    >
      <div className="px-6 pt-5 pb-6">
        <p className="lens-eyebrow">Submission</p>
        <h2 id="advanced-title" className="mt-1 text-h3 text-ink">Advanced options</h2>
        <p className="mt-2 text-ui text-ink-soft">
          Overrides for the rare case — leave empty for the default double-click, no-input, auto-child-analysis flow.
        </p>

        <div className="mt-5 space-y-5">
          <label className="block">
            <span className="lens-eyebrow block">Command</span>
            <input
              type="text"
              placeholder='e.g. "cmd.exe /c sample.exe --flag"'
              value={values.command}
              onChange={(e) => patch("command", e.target.value)}
              className="mt-1 w-full border border-rule-field bg-paper px-3 py-2 text-data text-ink focus:border-ink focus:outline-none"
            />
            <span className="mt-1 block text-meta text-ink-mute">
              How to launch the sample. Empty = the guest's default handler.
            </span>
          </label>

          <label className="block">
            <span className="lens-eyebrow block">Labels</span>
            <input
              type="text"
              placeholder="comma, separated, tags"
              value={values.labels}
              onChange={(e) => patch("labels", e.target.value)}
              className="mt-1 w-full border border-rule-field bg-paper px-3 py-2 text-data text-ink focus:border-ink focus:outline-none"
            />
            <span className="mt-1 block text-meta text-ink-mute">Free-form tags attached to the run; filterable later.</span>
          </label>

          {/* Interactive input + Analyze children live in the main
              RunSheet now — the dialog is just command + labels. */}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className={buttonClass({ variant: "secondary", size: "sm" })} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </dialog>
  );
}
